// Smart Daara - Student Management Page
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { userAPI, courseAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription
} from '../../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';
import { 
  GraduationCap, Plus, Search, MoreVertical, Trash2, Pencil,
  Loader2, Mail, Phone, Eye, UserCheck, UserX, KeyRound, FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';
import PasswordResetDialog from '../../components/shared/PasswordResetDialog';
import StudentImportDialog from '../../components/shared/StudentImportDialog';

export default function StudentManagement() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    password: '',
    class_id: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [studentsRes, classesRes] = await Promise.all([
        userAPI.listStudents({ tenant_id: user?.tenant_id }),
        courseAPI.listClasses({ tenant_id: user?.tenant_id })
      ]);
      setStudents(studentsRes.data?.items || studentsRes.data || []);
      setClasses(classesRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      phone: '',
      password: '',
      class_id: ''
    });
  };

  const handleCreateStudent = async () => {
    if (!formData.email || !formData.first_name || !formData.last_name || !formData.password) {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs requis' : 'Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      await userAPI.createStudent({
        ...formData,
        tenant_id: user?.tenant_id,
        role: 'student'
      });
      toast.success(language === 'fr' ? 'Étudiant créé avec succès!' : 'Student created successfully!');
      setCreateModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error creating student:', error);
      const message = error.response?.data?.detail || (language === 'fr' ? 'Erreur de création' : 'Creation error');
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditStudent = async () => {
    if (!formData.email || !formData.first_name || !formData.last_name) {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs requis' : 'Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      await userAPI.updateStudent(selectedStudent.id, {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        class_id: formData.class_id
      });
      toast.success(language === 'fr' ? 'Étudiant mis à jour!' : 'Student updated!');
      setEditModalOpen(false);
      setSelectedStudent(null);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error updating student:', error);
      toast.error(language === 'fr' ? 'Erreur de mise à jour' : 'Update error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStudent = async () => {
    if (!selectedStudent) return;

    setSaving(true);
    try {
      await userAPI.deleteStudent(selectedStudent.id);
      toast.success(language === 'fr' ? 'Étudiant supprimé!' : 'Student deleted!');
      setDeleteModalOpen(false);
      setSelectedStudent(null);
      loadData();
    } catch (error) {
      console.error('Error deleting student:', error);
      toast.error(language === 'fr' ? 'Erreur de suppression' : 'Delete error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (student) => {
    try {
      await userAPI.updateStudent(student.id, { is_active: !student.is_active });
      toast.success(language === 'fr' 
        ? (student.is_active ? 'Étudiant désactivé' : 'Étudiant activé')
        : (student.is_active ? 'Student deactivated' : 'Student activated')
      );
      loadData();
    } catch (error) {
      console.error('Error toggling student:', error);
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const openEditModal = (student) => {
    setSelectedStudent(student);
    setFormData({
      email: student.email || '',
      first_name: student.first_name || '',
      last_name: student.last_name || '',
      phone: student.phone || '',
      password: '',
      class_id: student.class_id || ''
    });
    setEditModalOpen(true);
  };

  const filteredStudents = students.filter(s => 
    s.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getClassName = (classId) => {
    const cls = classes.find(c => c.id === classId);
    return cls ? (cls.name_fr || cls.name) : '-';
  };

  return (
    <div className="space-y-6" data-testid="student-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-slate-900">
            {language === 'fr' ? 'Gestion des Étudiants' : 'Student Management'}
          </h1>
          <p className="text-slate-500">
            {language === 'fr' 
              ? `${filteredStudents.length} étudiant(s) enregistré(s)`
              : `${filteredStudents.length} student(s) registered`
            }
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setImportOpen(true)}
            data-testid="import-students-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Importer CSV' : 'Import CSV'}
          </Button>
          <Button 
            className="bg-emerald-700 hover:bg-emerald-800" 
            onClick={() => setCreateModalOpen(true)}
            data-testid="add-student-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Inscrire un Étudiant' : 'Add Student'}
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder={language === 'fr' ? 'Rechercher par nom ou email...' : 'Search by name or email...'}
              className="pl-9" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Students List */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-emerald-600" />
            {language === 'fr' ? 'Liste des Étudiants' : 'Student List'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <GraduationCap className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>{language === 'fr' ? 'Aucun étudiant trouvé' : 'No students found'}</p>
              <Button 
                variant="outline" 
                className="mt-3"
                onClick={() => setCreateModalOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Inscrire un étudiant' : 'Add a student'}
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">
                      {language === 'fr' ? 'Étudiant' : 'Student'}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">
                      {language === 'fr' ? 'Classe' : 'Class'}
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((student) => (
                    <tr key={student.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                            <GraduationCap className="w-5 h-5 text-emerald-700" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {student.first_name} {student.last_name}
                            </p>
                            {student.phone && (
                              <p className="text-xs text-slate-500 flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {student.phone}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-slate-600 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {student.email}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-slate-600">
                          {getClassName(student.class_id)}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <Badge className={student.is_active !== false
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-slate-100 text-slate-600'
                        }>
                          {student.is_active !== false 
                            ? (language === 'fr' ? 'Actif' : 'Active')
                            : (language === 'fr' ? 'Inactif' : 'Inactive')
                          }
                        </Badge>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditModal(student)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              {language === 'fr' ? 'Modifier' : 'Edit'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleActive(student)}>
                              {student.is_active !== false ? (
                                <>
                                  <UserX className="w-4 h-4 mr-2" />
                                  {language === 'fr' ? 'Désactiver' : 'Deactivate'}
                                </>
                              ) : (
                                <>
                                  <UserCheck className="w-4 h-4 mr-2" />
                                  {language === 'fr' ? 'Activer' : 'Activate'}
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedStudent(student);
                                setResetPasswordOpen(true);
                              }}
                              data-testid={`reset-password-student-${student.id}`}
                            >
                              <KeyRound className="w-4 h-4 mr-2" />
                              {language === 'fr' ? 'Réinitialiser mot de passe' : 'Reset password'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => {
                                setSelectedStudent(student);
                                setDeleteModalOpen(true);
                              }}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {language === 'fr' ? 'Supprimer' : 'Delete'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' ? 'Inscrire un Étudiant' : 'Add Student'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? 'Créez un compte étudiant pour votre école'
                : 'Create a student account for your school'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Prénom' : 'First Name'} *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => handleFormChange('first_name', e.target.value)}
                  placeholder="Mouhamed"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Nom' : 'Last Name'} *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => handleFormChange('last_name', e.target.value)}
                  placeholder="Diallo"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                placeholder="etudiant@ecole.sn"
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Téléphone' : 'Phone'}</Label>
              <Input
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
                placeholder="+221 77 000 0000"
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Classe' : 'Class'}</Label>
              <Select value={formData.class_id} onValueChange={(v) => handleFormChange('class_id', v)}>
                <SelectTrigger>
                  <SelectValue placeholder={language === 'fr' ? 'Sélectionner une classe' : 'Select a class'} />
                </SelectTrigger>
                <SelectContent>
                  {classes.map(cls => (
                    <SelectItem key={cls.id} value={cls.id}>
                      {cls.name_fr || cls.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Mot de passe' : 'Password'} *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => handleFormChange('password', e.target.value)}
                placeholder="••••••••"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateModalOpen(false); resetForm(); }}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleCreateStudent}
              disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Inscrire' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' ? 'Modifier l\'Étudiant' : 'Edit Student'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Prénom' : 'First Name'} *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => handleFormChange('first_name', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Nom' : 'Last Name'} *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => handleFormChange('last_name', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Téléphone' : 'Phone'}</Label>
              <Input
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Classe' : 'Class'}</Label>
              <Select value={formData.class_id} onValueChange={(v) => handleFormChange('class_id', v)}>
                <SelectTrigger>
                  <SelectValue placeholder={language === 'fr' ? 'Sélectionner une classe' : 'Select a class'} />
                </SelectTrigger>
                <SelectContent>
                  {classes.map(cls => (
                    <SelectItem key={cls.id} value={cls.id}>
                      {cls.name_fr || cls.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditModalOpen(false); setSelectedStudent(null); resetForm(); }}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleEditStudent}
              disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Enregistrer' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">
              {language === 'fr' ? 'Supprimer l\'Étudiant' : 'Delete Student'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? `Êtes-vous sûr de vouloir supprimer ${selectedStudent?.first_name} ${selectedStudent?.last_name}?`
                : `Are you sure you want to delete ${selectedStudent?.first_name} ${selectedStudent?.last_name}?`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleDeleteStudent}
              disabled={saving}
              variant="destructive"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Supprimer' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Modal */}
      <PasswordResetDialog
        open={resetPasswordOpen}
        onOpenChange={setResetPasswordOpen}
        user={selectedStudent}
        userId={selectedStudent?.user_id}
      />

      {/* Bulk CSV Import Modal */}
      <StudentImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={loadData}
      />
    </div>
  );
}
